import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Building2, User, Loader2 } from "lucide-react";
import { ContactFields } from "./ContactFields";
import { handoverApi } from "./handoverApi";

const EMPTY_P = { name: "", email: "", mobile: "" };

const TypeButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-semibold transition-colors ${
      active ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600 hover:border-amber-300"}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

export const DwellingClientDialog = ({ open, onOpenChange, dwelling, developers, onSaved, onAddDeveloper }) => {
  const [type, setType] = useState("purchaser");
  const [developerId, setDeveloperId] = useState("");
  const [purchaser, setPurchaser] = useState(EMPTY_P);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !dwelling) return;
    const c = dwelling.client;
    setType(c?.client_type || (developers.length ? "developer" : "purchaser"));
    setDeveloperId(c?.developer_id || "");
    setPurchaser(c?.purchaser ? { ...EMPTY_P, ...c.purchaser } : EMPTY_P);
  }, [open, dwelling, developers.length]);

  const save = async () => {
    setSaving(true);
    try {
      const body = type === "developer"
        ? { client_type: "developer", developer_id: developerId }
        : { client_type: "purchaser", purchaser };
      await handoverApi(`/dwellings/${dwelling.id}/client`, { method: "PUT", body });
      toast.success(`Client saved for ${dwelling.dwelling_number}`);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dwelling-client-dialog">
        <DialogHeader>
          <DialogTitle>Client for {dwelling?.dwelling_number}</DialogTitle>
          <DialogDescription>Each dwelling has one client — the Developer or a Purchaser, never both.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <TypeButton active={type === "developer"} onClick={() => setType("developer")} icon={Building2} label="Developer" testId="client-type-developer" />
          <TypeButton active={type === "purchaser"} onClick={() => setType("purchaser")} icon={User} label="Purchaser" testId="client-type-purchaser" />
        </div>
        {type === "developer" ? (
          developers.length ? (
            <div className="space-y-1.5">
              <Label>Developer *</Label>
              <Select value={developerId} onValueChange={setDeveloperId}>
                <SelectTrigger data-testid="client-developer-select"><SelectValue placeholder="Choose a developer" /></SelectTrigger>
                <SelectContent>
                  {developers.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`client-developer-option-${d.id}`}>
                      {d.company_name} — {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600" data-testid="client-no-developers">
              No developers yet for this site.{" "}
              <button type="button" className="font-semibold text-amber-700 underline" onClick={onAddDeveloper} data-testid="client-add-developer-link">
                Add a developer
              </button>
            </div>
          )
        ) : (
          <ContactFields value={purchaser} onChange={setPurchaser} prefix="purchaser" />
        )}
        {dwelling?.client && (
          <p className="text-xs text-slate-500" data-testid="client-change-note">
            Changing the client keeps the previous client in this dwelling's history.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={saving || (type === "developer" && !developerId)} className="btn-construction" data-testid="client-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
